import DehazeIcon from '@mui/icons-material/Dehaze';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid,
    Switch,
    TextField,
    Tooltip,
    useTheme
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { FaCalculator } from '@react-icons/all-files/fa/FaCalculator';
import { ChangeEvent, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { AgCheckboxGroupPanel } from '../../common/AgCheckboxGroupPanel';
import { PopperButton } from '../../common/PopperButton';
import { RootState } from '../../store';
import { canSeeStockColumn } from '../../utils';
import WarehouseSelector from '../stock/WarehouseSelector';
import { getCurrentUserPermissions } from '../user/redux/userSelectors';
import { priceKeyMap } from './Price';
import PricesMenuButton from './PricesMenuButton';
import { priceInitialColumns } from './priceTypes';
import {
    getApplyCalculations,
    getCheckboxFilters,
    getIsPriceTableLoading,
    getPriceSelectedWarehouse,
    getPriceTable,
    getPriceTableVisibleColumnKeys,
    getSelectedPricelistDate,
    getSelectedPricelistIds
} from './redux/priceSelectors';
import {
    openChoosePricelistsDialog,
    pricelistDateSelected,
    priceTableActions,
    setIsExporting,
    showAllArticlesChanged,
    showCommentsOnlyChanged,
    showNewArticlesOnlyChanged,
    toggleApplyCalculations,
    warehouseDateSelected,
    warehouseSelected
} from './redux/priceSlices';

const mapStateToProps = (state: RootState) => ({
    isLoading: getIsPriceTableLoading(state),
    selectedPricelistIds: getSelectedPricelistIds(state),
    checkboxFilters: getCheckboxFilters(state),
    selectedPricelistDate: getSelectedPricelistDate(state),
    selectedWarehouse: getPriceSelectedWarehouse(state),
    columnKeys: getPriceTableVisibleColumnKeys(state).map(c => c.key),
    applyCalculations: getApplyCalculations(state),
    priceTable: getPriceTable(state),
    currentUserPermissions: getCurrentUserPermissions(state)
});

type Props = ReturnType<typeof mapStateToProps>;

const PriceTableButtons = ({
    isLoading,
    selectedPricelistIds,
    checkboxFilters,
    selectedPricelistDate,
    selectedWarehouse,
    columnKeys,
    applyCalculations,
    priceTable,
    currentUserPermissions
}: Props) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const pricelistPeriodsSelected = () => selectedPricelistIds.length > 0;

    const handleCustDialogOpen = useCallback(() => dispatch(openChoosePricelistsDialog()), [dispatch]);

    const handleShowAllArticles = (event: ChangeEvent<HTMLInputElement>) => {
        dispatch(showAllArticlesChanged(event.target.checked));
    };

    const handleShowCommentsOnly = (event: ChangeEvent<HTMLInputElement>) => {
        dispatch(showCommentsOnlyChanged(event.target.checked));
    };

    const handleShowNewArticlesOnly = (event: ChangeEvent<HTMLInputElement>) => {
        dispatch(showNewArticlesOnlyChanged(event.target.checked));
    };

    const handleSetWarehouseDate = (newDate: Date | null) => {
        if (newDate != null) {
            dispatch(warehouseDateSelected(newDate.toISOString()));
        }
    };

    const handleSetPricelistDate = (newDate: Date | null) => {
        if (newDate != null) {
            dispatch(pricelistDateSelected(newDate.toISOString()));
        }
    };

    const handleSetWarehouse = (value: unknown) => {
        dispatch(warehouseSelected(value as number));
    };

    const handleColumnsSelected = (newColumns: string[]) => {
        dispatch(priceTableActions.columnsSelected(newColumns));
    };

    const handleUseCalculations = () => {
        dispatch(toggleApplyCalculations());
    };

    return (
        <Grid
            container
            alignItems="flex-start"
            justifyContent="space-between"
            direction="row"
            wrap="nowrap"
            sx={{ width: '100%' }}
        >
            <Grid item>
                <Grid container spacing={2}>
                    <Grid item>
                        <Button variant="contained" color="primary" onClick={handleCustDialogOpen}>
                            Choose pricelists
                        </Button>
                    </Grid>

                    <Grid item>
                        <WarehouseSelector
                            selectedWarehouse={selectedWarehouse.warehouse}
                            onSetWarehouse={handleSetWarehouse}
                        />
                    </Grid>

                    <Grid item>
                        <DatePicker
                            inputFormat="yyyy-MM-dd"
                            label="Warehouse date"
                            value={selectedWarehouse.date}
                            onChange={handleSetWarehouseDate}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    variant="standard"
                                    sx={{ marginTop: theme.spacing(1), width: 150 }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item>
                        <DatePicker
                            inputFormat="yyyy-MM-dd"
                            label="Pricelist date"
                            value={selectedPricelistDate}
                            onChange={handleSetPricelistDate}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    variant="standard"
                                    sx={{ marginTop: theme.spacing(1), width: 150 }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item>
                        <FormControlLabel
                            disabled={isLoading || !pricelistPeriodsSelected()}
                            control={
                                <Checkbox
                                    checked={checkboxFilters.showAllArticles}
                                    onChange={handleShowAllArticles}
                                />
                            }
                            label="Show all articles"
                        />
                    </Grid>

                    <Grid item>
                        <FormControlLabel
                            disabled={isLoading || !pricelistPeriodsSelected()}
                            control={
                                <Checkbox
                                    checked={checkboxFilters.showCommentsOnly}
                                    onChange={handleShowCommentsOnly}
                                />
                            }
                            label="Show comments only"
                        />
                    </Grid>
                    <Grid item>
                        <FormControlLabel
                            disabled={isLoading || !pricelistPeriodsSelected()}
                            control={
                                <Checkbox
                                    checked={checkboxFilters.showNewArticlesOnly}
                                    onChange={handleShowNewArticlesOnly}
                                />
                            }
                            label="Show new articles only"
                        />
                    </Grid>
                    <Grid item>
                        <Tooltip title={`Apply calculations (${priceKeyMap.toggleApplyCalculations})`}>
                            <Box
                                display="flex"
                                alignItems="center"
                                sx={{
                                    border: '1px solid rgba(128, 128, 128, .2)',
                                    borderRadius: 16,
                                    paddingLeft: '9px'
                                }}
                            >
                                <FaCalculator
                                    color={applyCalculations ? theme.palette.primary.main : 'rgba(0,0,0,.3)'}
                                    size={22}
                                />
                                <Switch
                                    aria-label="Apply calculations"
                                    checked={applyCalculations}
                                    onChange={handleUseCalculations}
                                    role="switch"
                                />
                            </Box>
                        </Tooltip>
                    </Grid>
                    <Grid item>
                        <PricesMenuButton
                            onDownloadClicked={() => dispatch(setIsExporting(true))}
                            showTooltipsChangedAction={priceTableActions.showTooltipsChanged}
                            tooltipsVisible={priceTable.showTooltips}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <CircularProgress sx={!isLoading ? { visibility: 'hidden' } : undefined} />
                    </Grid>

                    <Grid item xs={6}>
                        <PopperButton btnIcon={<DehazeIcon />} btnTitle="Choose columns">
                            <AgCheckboxGroupPanel
                                available={priceInitialColumns.filter(colDef =>
                                    canSeeStockColumn(colDef.field, currentUserPermissions)
                                )}
                                selected={columnKeys}
                                setSelected={handleColumnsSelected}
                            />
                        </PopperButton>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default connect(mapStateToProps)(PriceTableButtons);
